import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

// Server contracts: packages/api/src/routes/settings/go-live.routes.ts,
// routes/admin/licenses.routes.ts (GET /license/installation),
// services/onboarding/test-data-check.service.ts,
// services/licensing/installed-license.service.ts, licence-enforcement.ts

export interface TestDataSample {
  id: string;
  label: string;
  detail: string | null;
}

export interface TestDataFinding {
  key: string;
  label: string;
  description: string;
  count: number;
  breakdown?: Array<{ label: string; count: number }>;
  samples: TestDataSample[];
}

export interface TestDataCheckResult {
  checkedAt: string;
  clean: boolean;
  findings: TestDataFinding[];
  /** Series holding numbers the SFE drew; `used` excludes a takeover's numbers. */
  consumedNumbers: Array<{
    establishmentId: string | null;
    invoiceType: string;
    fiscalYear: number;
    lastNumber: number;
    used: number;
  }>;
  /** Series initialised with the last number a former system issued. */
  takeovers: Array<{
    establishmentId: string | null;
    invoiceType: string;
    fiscalYear: number;
    lastIssued: number;
  }>;
}

export type InstalledLicenseState =
  | 'NON_APPLICABLE'
  | 'ABSENTE'
  | 'VALIDE'
  | 'EXPIREE'
  | 'REVOQUEE'
  | 'INVALIDE'
  | 'INVERIFIABLE';

export type LicenceEnforcementPhase = 'AUCUNE' | 'AVERTISSEMENT' | 'GRACE' | 'BLOQUEE';

/**
 * Issuing suspended while the revocation list this install reads is older
 * than its newest anchor, or while it holds no verified fiscal input although
 * it held one: the issuing routes refuse with this code and message
 * (middleware/licence-issuing-guard.ts).
 */
export interface IssuingSuspension {
  code: 'LISTE_REVOCATION_EN_RECUL' | 'CONFIGURATION_FISCALE_CLE_RETIREE' | 'CONFIGURATION_FISCALE_NON_VERIFIEE';
  message: string;
}

/** What the licence of this install restricts; phase AUCUNE online. */
export interface LicenceEnforcement {
  phase: LicenceEnforcementPhase;
  state: InstalledLicenseState;
  message: string;
  graceEndsAt: string | null;
  daysLeft: number | null;
  /**
   * Day (YYYY-MM-DD) of the last verified revocation list, once it is 20
   * days old or more: the state of this SFE version cannot be checked since
   * (#622 D). Null otherwise and online.
   */
  sfeStateUnverifiableSince?: string | null;
  /** Null while issuing is not suspended, and online. */
  issuingSuspension?: IssuingSuspension | null;
  /**
   * Day (YYYY-MM-DD) a licence the former publisher key signed stops being
   * accepted, from 30 days before it. Null otherwise and online.
   */
  formerKeyLicenceUntil?: string | null;
}

export interface InstalledLicense {
  state: InstalledLicenseState;
  applicable: boolean;
  message: string;
  license: {
    licenseId: string;
    companyName: string;
    ifu: string | null;
    plan: string;
    maxUsers: number | null;
    issuedAt: string;
    expiresAt: string;
  } | null;
  ifuMatchesCompany: boolean | null;
  revocations: { state: string; issuedAt: string | null; message: string } | null;
  enforcement: LicenceEnforcement;
  /** Null while issuing is not suspended, and online. */
  issuingSuspension?: IssuingSuspension | null;
}

export const goLiveKeys = {
  all: ['go-live'] as const,
  testData: () => [...goLiveKeys.all, 'test-data'] as const,
  licence: () => [...goLiveKeys.all, 'licence'] as const,
  enforcement: () => [...goLiveKeys.all, 'licence-enforcement'] as const,
};

/** GET /api/settings/mise-en-service/donnees-de-test — read only. */
export function useTestDataCheck() {
  return useQuery({
    queryKey: goLiveKeys.testData(),
    queryFn: () =>
      apiClient.get<TestDataCheckResult>('/settings/mise-en-service/donnees-de-test'),
  });
}

/** GET /api/settings/mise-en-service/licence */
export function useInstalledLicense() {
  return useQuery({
    queryKey: goLiveKeys.licence(),
    queryFn: () => apiClient.get<InstalledLicense>('/settings/mise-en-service/licence'),
  });
}

/** PUT /api/settings/mise-en-service/licence — verifies the key offline and stores it. */
export function useInstallLicense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      apiClient.put<InstalledLicense>('/settings/mise-en-service/licence', { key }),
    onSuccess: (status) => {
      queryClient.setQueryData(goLiveKeys.licence(), status);
      void queryClient.invalidateQueries({ queryKey: goLiveKeys.enforcement() });
    },
  });
}

/** GET /api/license/installation — any signed-in user; drives the licence banner. */
export function useLicenceEnforcement() {
  return useQuery({
    queryKey: goLiveKeys.enforcement(),
    queryFn: () => apiClient.get<LicenceEnforcement>('/license/installation'),
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
    retry: false,
  });
}
